/**
 * A machine for loading, updating, and creating business objects of an entity
 * @typedef SweftDataMachineEntityDataActorMachine
 * @type {StateMachine<SweftDataUnknownDataActorMachineContext, none, SweftDataUnknownDataActorMachineTypestate>}
 */

import { VanguardService } from "@app/services/vanguardService";
import { SequencesService } from "@app/services/sequencesService";
import { generateOptimisticDataMachine } from "@app/data/machine/actors/optimisticDataActorMachine";
import { mapEntityData } from "@app/data/mapping/mapEntityData";

/**
 * @interface SweftDataMachineEntityDataActorMachineContext
 * @extends SweftOptimisticDataMachineContext
 * @prop {"id"} keyProperty
 */


/**
 * @param entity
 * @param type
 * @returns {SweftOptimisticDataMachineGeneratorProps}
 */
// eslint-disable-next-line max-lines-per-function
export const generateEntityDataMachineGeneratorProps = ({ dataActorOptions }) => {
    const { entity, type, schemaTreeExclusionList, relatedProjectionPaths, projectionAttributeList, loadOnSpawn, noParent, dataActorId, dataActorSavedQueryObject, receiveEvaluatedBobj, entityFormulaPathMap, formulaForBackendEval, dataMappingModuleConfig, preFilterEntityMap, savedQuery } = dataActorOptions;

    const keyProperty = "id";
    return {
        dataActorId,
        loadOnSpawn,
        savedQuery,
        entity,
        type: type ?? `${entity}DataActor`,
        keyProperty,
        noParent,
        projectionAttributeList,
        receiveEvaluatedBobj,
        entityFormulaPathMap,
        preFilterEntityMap,
        formulaForBackendEval,
        createMachineOptions: {
            services: {
                createService: async (context) => {
                    const { object } = context;
                    return new Promise((resolve, reject) => {
                        VanguardService.data.createBusinessObject({ businessObject: object })
                            .then(() => {
                                resolve({
                                    newObject: {
                                        ...object,
                                    }
                                });
                            });
                    });
                },
                generatedIdService: async (context) => {
                    const { gidAttributesList, object } = context;
                    /**
                     * Returns the object with the generated id properties populated with the next sequence number.
                     */
                    const generatedIdAttributeList = await Promise.all(gidAttributesList.map((attribute) => {
                        return new Promise((resolve, reject) => {
                            SequencesService.data.generatedIdService({ attribute })
                                .then((sequenceNumber) => {
                                    resolve({
                                        key: attribute.name,
                                        value: sequenceNumber
                                    });
                                });
                        });
                    }));

                    const generatedIdObject = generatedIdAttributeList.reduce((currentObjVal, nextGeneratedIdObj) => {
                        return {
                            ...currentObjVal,
                            [nextGeneratedIdObj.key]: nextGeneratedIdObj.value
                        };
                    }, {});

                    return {
                        newObject: {
                            ...object,
                            ...generatedIdObject
                        },
                        generatedIdObject
                    };
                }
            }
        },
        updateMachineOptions: {
            services: {
                updateService: async (context) => {
                    const { object, fieldBeingUpdated } = context;
                    return new Promise((resolve, reject) => {
                        VanguardService.data.updateBusinessObject({ businessObject: object, fieldBeingUpdated, keyProperty })
                            .then(() => {
                                resolve({
                                    newObject: {
                                        ...context
                                    }
                                });
                            });
                    });
                }
            }
        },
        loadMachineOptions: {
            services: {
                /**
                 * Called when the LOAD_DATA event is received
                 */
                loadService: async (context, event) => {
                    const { projectionAttributeList: projectionAttributeListFromContext = [], additionalJsonLogicQueryObject: additionalJsonLogicQueryObjectFromContext, preFilterEntityMap: preFilterEntityMapFromContext } = context;

                    const additionalJsonLogicQueryObjectList = [];
                    if (dataActorSavedQueryObject) {
                        additionalJsonLogicQueryObjectList.push(dataActorSavedQueryObject);
                    }
                    if (additionalJsonLogicQueryObjectFromContext) {
                        additionalJsonLogicQueryObjectList.push(additionalJsonLogicQueryObjectFromContext);
                    }

                    let additionalJsonLogicQueryObject = null;
                    if (additionalJsonLogicQueryObjectList.length > 0) {
                        additionalJsonLogicQueryObject = { "and": additionalJsonLogicQueryObjectList };
                    }

                    let projectionAttributeListForLoadService = [...projectionAttributeList, ...projectionAttributeListFromContext];
                    let entityToLoad = entity;

                    if (dataMappingModuleConfig?.enabled === true) {
                        const { entityDataMappingConfigMap, alternateWorkspaceEntityToLoad } = dataMappingModuleConfig.options;
                        entityToLoad = alternateWorkspaceEntityToLoad;
                        projectionAttributeListForLoadService = [...projectionAttributeListForLoadService, ...entityDataMappingConfigMap[entityToLoad].mappingProjectionList];
                    }

                    const projectionAttributeListWithoutFormulaList = projectionAttributeListForLoadService.filter((projection) => !entityFormulaPathMap?.[projection]);

                    const newObjs = await VanguardService.data.getBusinessObjectsOfEntity({
                        entity: entityToLoad,
                        schemaTreeExclusionList,
                        relatedProjectionPaths,
                        projectionAttributeList: projectionAttributeListWithoutFormulaList,
                        additionalJsonLogicQueryObject,
                        receiveEvaluatedBobj,
                        formulaForBackendEval,
                        preFilterEntityMap: preFilterEntityMapFromContext,
                        savedQueryId: savedQuery?.id
                    });

                    if (dataMappingModuleConfig?.enabled === true) {
                        const { entityDataMappingConfigMap } = dataMappingModuleConfig.options;
                        const entityDataMappingConfigObj = entityDataMappingConfigMap[entityToLoad];
                        // return newObjs if API call fails the error is returned here.
                        if (!Array.isArray(newObjs)) {
                            return { loadedData: newObjs };
                        }
                        const mappedData = mapEntityData({ entityDataMappingConfigObj, bobjList: newObjs });
                        return { loadedData: mappedData, requestProjectionAttributeList: projectionAttributeListForLoadService, dataMappingModuleConfig };
                    }

                    return { loadedData: newObjs, requestProjectionAttributeList: projectionAttributeListForLoadService };
                },
            }
        },
        services: {
            /**
             * Called by the machine when loading on spawn
             */
            loadService: async (context, event) => {
                const { projectionAttributeList: projectionAttributeListFromContext = [] } = context;
                let additionalJsonLogicQueryObject = null;
                if (dataActorSavedQueryObject) {
                    additionalJsonLogicQueryObject = dataActorSavedQueryObject;
                }

                let entityToLoad = entity;
                let projectionAttributeListForLoadService = [ ...projectionAttributeListFromContext];
                if (dataMappingModuleConfig?.enabled === true) {
                    const { entityDataMappingConfigMap, alternateWorkspaceEntityToLoad } = dataMappingModuleConfig.options;
                    entityToLoad = alternateWorkspaceEntityToLoad;
                    projectionAttributeListForLoadService = [...projectionAttributeListForLoadService, ...entityDataMappingConfigMap[entityToLoad].mappingProjectionList];
                }

                const projectionAttributeListWithoutFormulaList = projectionAttributeListForLoadService.filter((projection) => !entityFormulaPathMap?.[projection]);

                const newObjs = await VanguardService.data.getBusinessObjectsOfEntity({
                    entity: entityToLoad,
                    schemaTreeExclusionList,
                    relatedProjectionPaths,
                    projectionAttributeList: projectionAttributeListWithoutFormulaList,
                    additionalJsonLogicQueryObject,
                    receiveEvaluatedBobj,
                    formulaForBackendEval,
                    preFilterEntityMap,
                    savedQueryId: savedQuery?.id
                });

                if (dataMappingModuleConfig?.enabled === true) {
                    const { entityDataMappingConfigMap } = dataMappingModuleConfig.options;
                    const entityDataMappingConfigObj = entityDataMappingConfigMap[entityToLoad];
                    // return newObjs if API call fails the error is returned here.
                    if (!Array.isArray(newObjs)) {
                        return { loadedData: newObjs };
                    }
                    const mappedData = mapEntityData({ entityDataMappingConfigObj, bobjList: newObjs });
                    return { loadedData: mappedData, requestProjectionAttributeList: projectionAttributeListForLoadService, dataMappingModuleConfig };
                }

                return {
                    loadedData: newObjs,
                    requestProjectionAttributeList: projectionAttributeListForLoadService
                };
            },
            loadDataFilteredForTransientData: async (context, event) => {
                const { transientData, projectionAttributeList: projectionAttributeListFromContext } = context;

                const transientObjectIdList = transientData.map((transientObj) => transientObj?.object?.id);
                if (transientObjectIdList.length === 0) {
                    return {
                        loadedData: []
                    };
                }

                const projectionAttributeListWithoutFormulaList = projectionAttributeListFromContext?.filter((projection) => !entityFormulaPathMap?.[projection]) ?? [];

                const preFilterEntityMap = {
                    [entity]: [{
                        attribute: "id",
                        operator: "SweftOperators.SweftInList",
                        value: transientObjectIdList
                    }]
                };

                return new Promise((resolve, reject) => {
                    VanguardService.data.getBusinessObjectsOfEntity({ entity, schemaTreeExclusionList, projectionAttributeList: projectionAttributeListWithoutFormulaList, receiveEvaluatedBobj, preFilterEntityMap }).then((newObjs) => {
                        resolve({
                            loadedData: newObjs,
                            requestProjectionAttributeList: projectionAttributeListFromContext,
                        });
                    }).catch((error) => {
                        console.log(error);
                        reject();
                    });
                });
            },
        }
    };
};

/**
 * @type {SweftDataMachineEntityDataActorMachine}
 */
export const generateEntityDataActorMachine = ({ dataActorOptions }) => generateOptimisticDataMachine(generateEntityDataMachineGeneratorProps({ dataActorOptions }));
